import React, { Component } from "react";
import PropTypes from "prop-types";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Container,
  Row,
  Col,
  Button,
  FormGroup,
  Label,
  UncontrolledPopover,
  PopoverHeader,
  PopoverBody,
} from "reactstrap";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";

import loginImg from "../../assets/img/agent-login.png";
import "../../assets/scss/login.scss";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FooterLogin from "components/VerticalLayout/FooterLogin";
import Swal from "sweetalert2";
import { post } from "helpers/api_helper";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

function base64QueryParamDecode(value) {
  if (!value) return "";

  // URLSearchParams decodes %xx but some senders don't encode '+' (space issue)
  const cleaned = String(value).replace(/ /g, "+");

  // Support URL-safe base64 too
  const normalized = cleaned.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "===".slice((normalized.length + 3) % 4);

  try {
    const binary = window.atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    if (typeof TextDecoder !== "undefined") {
      return new TextDecoder("utf-8").decode(bytes);
    }
    return binary;
  } catch (e) {
    return "";
  }
}
const strongPasswordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()_+\-=[\]{};':"\\|,.<>/?]).{12,}$/;

function getPasswordChecks(password) {
  const value = password || "";
  const specialCharRegex = /[!@#$%^&*?]/;

  return {
    minLength: value.length >= 12,
    uppercase: /[A-Z]/.test(value),
    lowercase: /[a-z]/.test(value),
    number: /\d/.test(value),
    special: specialCharRegex.test(value),
  };
}


const ResetPasswordSchema = Yup.object().shape({
  passKey: Yup.string()
    .required("Recovery token is required"),
  password: Yup.string()
    .required("New password is required")
    .matches(
      strongPasswordRegex,
      "Min 12 chars, with uppercase, lowercase, number & special character"
    ),

  confirmPassword: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("password"), null], "Passwords must match"),
});

class ResetPasswords extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showPassword: false,
      showPasswordTooltip: false,
      turnstileToken: null,
      initialValues: {
        userName: "",
        userType: "",
        passKey: "",
        password: "",
        confirmPassword: "",
      },
    };
    this.turnstileRef = null;
    this.turnstileWidgetId = null;
    this.turnstileRetry = null;
  }
componentDidMount() {
  const token = localStorage.getItem("authToken");

  if (token) {
    this.props.history.replace("/dashboard");
    return;
  }

  const preload = document.createElement("link");
  preload.rel = "preload";
  preload.as = "image";
  preload.href = loginImg;
  document.head.appendChild(preload);

  const searchParams = new URLSearchParams(this.props.location?.search || "");
  const userFromUrl = base64QueryParamDecode(searchParams.get("user"));
  const typeFromUrl = base64QueryParamDecode(searchParams.get("type"));

  if (userFromUrl || typeFromUrl) {
    this.setState((prev) => ({
      initialValues: {
        ...prev.initialValues,
        userName: userFromUrl || prev.initialValues.userName,
        userType: typeFromUrl || prev.initialValues.userType,
      },
    }));
  }

  if (TURNSTILE_SITE_KEY) {
    this.loadTurnstile();
  }
}

  componentWillUnmount() {
    this.destroyTurnstile();
  }

  loadTurnstile = () => {
    if (!TURNSTILE_SITE_KEY) return;

    if (window.turnstile && this.turnstileRef) {
      if (this.turnstileWidgetId != null) {
        window.turnstile.remove(this.turnstileWidgetId);
        this.turnstileWidgetId = null;
      }

      this.turnstileWidgetId = window.turnstile.render(this.turnstileRef, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "light",
        callback: (token) => this.setState({ turnstileToken: token }),
        "expired-callback": () => this.setState({ turnstileToken: null }),
        "error-callback": () => this.setState({ turnstileToken: null }),
      });
      return;
    }

    this.turnstileRetry = setTimeout(this.loadTurnstile, 200);
  };

  resetTurnstile = () => {
    this.setState({ turnstileToken: null });
    if (window.turnstile && this.turnstileWidgetId != null) {
      window.turnstile.reset(this.turnstileWidgetId);
    }
  };

  destroyTurnstile = () => {
    if (this.turnstileRetry) {
      clearTimeout(this.turnstileRetry);
      this.turnstileRetry = null;
    }
    if (window.turnstile && this.turnstileWidgetId != null) {
      window.turnstile.remove(this.turnstileWidgetId);
      this.turnstileWidgetId = null;
    }
  };
 
  togglePassword = () => {
    this.setState((prev) => ({ showPassword: !prev.showPassword }));
  };


  handleValidSubmit = async (values, { setSubmitting, resetForm }) => {
    if (TURNSTILE_SITE_KEY && !this.state.turnstileToken) {
      Swal.fire({
        icon: "warning",
        title: "Security check required",
        text: "Please complete the security check.",
        confirmButtonText: "OK",
      });
      setSubmitting(false);
      return;
    }

    const payload = {
      userName: values.userName,
      userType: values.userType,
      passKey: values.passKey,
      newPassword: values.password,
      turnstileToken: this.state.turnstileToken,
    };
  
    try {
      // Loading alert
      Swal.fire({
        title: "Resetting password...",
        text: "Please wait",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });
  
      const res = await post(`user/resetpassword`, payload);
  
      Swal.fire({
        icon: "success",
        title: "Password reset successful",
        //text: res?.data?.message || "You can now login with your new password.",
        confirmButtonText: "OK",
      }).then(() => {
        // optional:
        //resetForm();
        this.props.history.push("/login");
      });
    } catch (err) {
      this.resetTurnstile();
      // Try to extract a meaningful message from backend
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.msg ||
        err?.message;
  
      Swal.fire({
        icon: "error",
        title: "Reset failed",
        text: apiMsg || "Something went wrong. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setSubmitting(false);
    }
  };
  

  render() {
    const { showPassword, initialValues, turnstileToken } = this.state;
    const turnstileRequired = Boolean(TURNSTILE_SITE_KEY);

    return (
      <React.Fragment>
        <ToastContainer autoClose={2000} />
        <div className="account-pages">
          <Container>
            <Row className="justify-content-center align-items-center vh-100">
              <Col lg={11} xl={10}>
                <div className="login-shell-card overflow-hidden my-4">
                  <Row className="g-0 align-items-center">
                    <Col lg={6} md={6}>
                      <div className="login-form-panel">
                        <div className="login-heading">
                          <h1>Reset Password</h1>
                        </div>

                        <div className="p-2">
                          <Formik
                            enableReinitialize
                            initialValues={initialValues}
                            validationSchema={ResetPasswordSchema}
                            onSubmit={this.handleValidSubmit}
                          >
                            {({ isSubmitting, values }) => {
                              const checks = getPasswordChecks(values?.password);
                              const getCheckIconClass = (isOk) =>
                                isOk
                                  ? "fas fa-check-circle text-success"
                                  : "far fa-circle text-muted";

                              const getCheckTextClass = (isOk) =>
                                isOk ? "text-success" : "text-muted";
                              const canSubmit =
                                !isSubmitting &&
                                (!turnstileRequired || Boolean(turnstileToken));

                              return (
                              <Form>
                                <FormGroup>
                                  <Label>Recovery token</Label>
                                  <Field
                                    name="passKey"
                                    type="text"
                                    className="form-control border-r-50"
                                    placeholder="Enter Passkey"
                                  />
                                  <ErrorMessage
                                    name="passKey"
                                    component="div"
                                    className="text-danger"
                                  />
                                </FormGroup>

                                <FormGroup className="position-relative">
                                  <Label>New password</Label>
                                  <Field
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    className="form-control border-r-50"
                                    placeholder="Enter new password"
                                    onFocus={() => this.setState({ showPasswordTooltip: true })}
                                    onBlur={() => this.setState({ showPasswordTooltip: false })}
                                  />
                                  <span
                                    onClick={this.togglePassword}
                                    style={{
                                      position: "absolute",
                                      top: "38px",
                                      right: "10px",
                                      cursor: "pointer",
                                      color: "#666",
                                    }}
                                  >
                                    {showPassword ? <i className="far fa-eye" /> : <i className="far fa-eye-slash" />}
                                  </span>
                                  <ErrorMessage
                                    name="password"
                                    component="div"
                                    className="text-danger"
                                  />
                                  <UncontrolledPopover
                                      placement="right"
                                      target="password"
                                      trigger="focus"
                                    >
                                      <PopoverHeader>Secure password tips:</PopoverHeader>
                                      <PopoverBody>
                                      <ul className="mb-0 pl-0" style={{ listStyle: "none" }}>
                                        <li className={`d-flex align-items-center mb-1 ${getCheckTextClass(checks.minLength)}`}>
                                          <i className={`${getCheckIconClass(checks.minLength)} mr-2`} />
                                          Minimum 12 characters
                                        </li>
                                        <li className={`d-flex align-items-center mb-1 ${getCheckTextClass(checks.uppercase)}`}>
                                          <i className={`${getCheckIconClass(checks.uppercase)} mr-2`} />
                                          At least one uppercase letter (A–Z)
                                        </li>
                                        <li className={`d-flex align-items-center mb-1 ${getCheckTextClass(checks.lowercase)}`}>
                                          <i className={`${getCheckIconClass(checks.lowercase)} mr-2`} />
                                          At least one lowercase letter (a–z)
                                        </li>
                                        <li className={`d-flex align-items-center mb-1 ${getCheckTextClass(checks.number)}`}>
                                          <i className={`${getCheckIconClass(checks.number)} mr-2`} />
                                          At least one number (0–9)
                                        </li>
                                        <li className={`d-flex align-items-center ${getCheckTextClass(checks.special)}`}>
                                          <i className={`${getCheckIconClass(checks.special)} mr-2`} />
                                          At least one special character (!@#$%^&*)
                                        </li>
                                      </ul>
                                      </PopoverBody>
                                    </UncontrolledPopover>
                                </FormGroup>

                                <FormGroup>
                                  <Label>Confirm Password</Label>
                                  <Field
                                    name="confirmPassword"
                                    type={showPassword ? "text" : "password"}
                                    className="form-control border-r-50"
                                    placeholder="Confirm password"
                                  />
                                  <ErrorMessage
                                    name="confirmPassword"
                                    component="div"
                                    className="text-danger"
                                  />
                                </FormGroup>

                                {turnstileRequired ? (
                                  <div
                                    className="login-turnstile"
                                    ref={(el) => {
                                      this.turnstileRef = el;
                                    }}
                                  />
                                ) : null}

                                <div className="mt-3 d-grid">
                                  <Button
                                    type="submit"
                                    className="btn btn-login btn-block"
                                    disabled={!canSubmit}
                                  >
                                    Reset Password
                                  </Button>
                                </div>

                                <div className="mt-4">
                                  <Link to="/login" className="login-forgot-link">
                                    <i className="mdi mdi-lock me-1" /> Back to login
                                  </Link>
                                </div>
                              </Form>
                              );
                            }}
                          </Formik>
                        </div>
                      </div>
                    </Col>
                    <Col lg={6} md={6} className="d-none d-md-block">
                      <div className="login-art-panel">
                        <img
                          src={loginImg}
                          alt="Reset password"
                          className="img-fluid login-art-img"
                          fetchPriority="high"
                          decoding="async"
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Container>

          <FooterLogin />
        </div>
      </React.Fragment>
    );
  }
}

ResetPasswords.propTypes = {
  history: PropTypes.object,
  location: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {};
};

export default withRouter(connect(mapStateToProps, null)(ResetPasswords));
